import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import type { DocumentNode } from 'graphql';
import type { RefresherCustomEvent } from '@ionic/react';
import type { PostCardFieldsFragment } from '../../../../types/graphql-generated';

export const POSTS_PER_PAGE = 20;

type PageVariables = { limit?: number | null; offset?: number | null };

export interface PaginatedPosts {
  posts: PostCardFieldsFragment[];
  loading: boolean;
  error: unknown;
  hasMore: boolean;
  onLoadMore: (ev: CustomEvent<void>) => Promise<void>;
  onRefresh: (ev: RefresherCustomEvent) => Promise<void>;
}

// Offset-based pagination for a list of posts, shared by the followed-channels
// timeline and a single channel's post list. Both queries return the same
// PostCardFields shape under a different root field, so the caller passes the
// query, that root field name and the non-pagination variables.
export function usePaginatedPosts<TData, TVars extends PageVariables>(
  query: DocumentNode,
  field: keyof TData & string,
  baseVariables: Omit<TVars, 'limit' | 'offset'>,
  options?: { fetchPolicy?: 'cache-first' | 'cache-and-network' | 'network-only' },
): PaginatedPosts {
  const [hasMore, setHasMore] = useState(true);

  const variablesFor = (nextOffset: number) =>
    ({ ...baseVariables, limit: POSTS_PER_PAGE, offset: nextOffset } as unknown as TVars);

  const { data, loading, error, fetchMore, refetch } = useQuery<TData, TVars>(query, {
    variables: variablesFor(0),
    fetchPolicy: options?.fetchPolicy,
  });

  const posts = ((data?.[field] as unknown) as PostCardFieldsFragment[] | undefined) ?? [];

  // The next page starts right after what we already have, so we derive the
  // offset from the loaded list rather than tracking it separately. That keeps
  // pagination correct when an external refetch (e.g. a mutation's
  // refetchQueries) resets the cached list back to page one.
  const length = posts.length;
  const headId = posts[0]?.id;
  const previousLength = useRef(length);
  const previousHeadId = useRef(headId);
  useEffect(() => {
    // Our own onLoadMore appends, so it never shrinks the list or changes the
    // head. A shorter list or a new head means the cache was reset externally
    // (a mutation's refetchQueries, say) — re-enable the scroller unless the
    // refreshed first page is already short (the real end).
    if (length < previousLength.current || headId !== previousHeadId.current) {
      setHasMore(length >= POSTS_PER_PAGE);
    }
    previousLength.current = length;
    previousHeadId.current = headId;
  }, [length, headId]);

  const onLoadMore = async (ev: CustomEvent<void>) => {
    let fetchedCount = 0;

    await fetchMore({
      variables: variablesFor(posts.length),
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const prevList = (prev as Record<string, unknown[]>)[field] ?? [];
        const moreList = (fetchMoreResult as Record<string, unknown[]>)[field] ?? [];
        fetchedCount = moreList.length;
        return { ...prev, [field]: [...prevList, ...moreList] } as TData;
      },
    });

    // A short page means we reached the end: stop the scroller from firing on
    // empty results. A later pull-to-refresh re-enables it.
    setHasMore(fetchedCount >= POSTS_PER_PAGE);
    (ev.target as HTMLIonInfiniteScrollElement).complete();
  };

  const onRefresh = async (ev: RefresherCustomEvent) => {
    try {
      setHasMore(true);
      await refetch(variablesFor(0));
    } finally {
      ev.detail.complete();
    }
  };

  return { posts, loading, error, hasMore, onLoadMore, onRefresh };
}
